// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import type { Permission } from '@vn-grc/shared';
import { usePermissionContext } from '../contexts/PermissionContext.js';

/**
 * Sprint 43.8.0 Wave 1.3 — type-level regression guard.
 *
 * The `Permission` type from @vn-grc/shared is a union of permission VALUE
 * string literals (e.g. `'case:read' | 'incident_ops_coordination:read' | ...`).
 * Typing the `permission` parameter as `Permission` rejects any other string at
 * compile time — including enum KEY names like `'INCIDENT_OPS_COORDINATION_READ'`
 * which were the root cause of the incident-ops outage documented in the sprint
 * 43.8.0 spec.
 *
 * Callers may pass either:
 *   - `Permission.CASE_READ`              (enum reference — preferred)
 *   - `'case:read'`                       (valid VALUE string literal)
 *
 * Callers may NOT pass:
 *   - `'CASE_READ'`                       (enum KEY name — compile error)
 *   - any typo'd or arbitrary string      (compile error)
 */
export function usePermission(permission: Permission): boolean {
  const { hasPermission } = usePermissionContext();
  return hasPermission(permission);
}

export function useAnyPermission(permissions: Permission[]): boolean {
  const { hasAnyPermission } = usePermissionContext();
  return hasAnyPermission(permissions);
}
